import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Truck, User, MapPin, Clock, Paperclip, FileText, ExternalLink, Printer, CheckCircle, FileCheck, ChevronDown, ChevronUp, Package, ClipboardCheck, ShieldCheck } from 'lucide-react';
import OrderPreview from './OrderPreview.js';
import MOCPreviewContent from './MOCPreviewContent.js';
import QCPreview from './QCPreview.js';
import DCPreviewContent from './DCPreviewContent.js';
import HorizontalTimeline from './components/HorizontalTimeline.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  return `${addr.address || ""}, ${addr.city || ""}, ${addr.state || ""} - ${addr.pincode || ""}`;
};

const DeliveredDetail = ({ db }) => {
  const { id } = useParams(); // This is a dispatch ID
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [firms, setFirms] = useState([]);
  const [activeTab, setActiveTab] = useState('shipments');
  const [expandedSections, setExpandedSections] = useState({}); // { dispatchId: { sectionKey: true/false } }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const firmSnap = await getDocs(collection(db, "firms"));
        const fetchedFirms = firmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFirms(fetchedFirms);

        // 1. Get the current dispatch to find orderId
        const currentDocRef = doc(db, "dispatches", id);
        const currentDocSnap = await getDoc(currentDocRef);

        if (currentDocSnap.exists()) {
          const currentMData = currentDocSnap.data();
          const orderId = currentMData.orderId;

          if (orderId) {
            // 2. Fetch Order
            const orderSnap = await getDoc(doc(db, "orders", orderId));
            if (orderSnap.exists()) {
              const oData = { id: orderSnap.id, ...orderSnap.data() };
              setOrder(oData);

              // 3. Fetch Customer
              if (oData.customerID) {
                const custSnap = await getDoc(doc(db, "customers", oData.customerID));
                if (custSnap.exists()) {
                  setCustomer({ id: custSnap.id, ...custSnap.data() });
                }
              }

              // 4. Subscribe to ALL dispatches for this order
              const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
              const unsubscribe = onSnapshot(q, (snapshot) => {
                const dispatchesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setDispatches(dispatchesList.sort((a, b) => (a.mocNo > b.mocNo ? 1 : -1)));
              });
              return () => unsubscribe();
            }
          }
        } else {
          alert("Dispatch not found");
          navigate('/ops/delivered');
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, id, navigate]);

  const toggleSection = (dispatchId, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [dispatchId]: {
        ...(prev[dispatchId] || {}),
        [section]: !prev[dispatchId]?.[section]
      }
    }));
  };

  const processDCPreviewData = (mData, orderData, customerData) => {
    const reconstructedComponents = (mData.components || []).map(mc => {
      let originalBaseQty = mc.perSet;
      if (!originalBaseQty && orderData) {
        orderData.boms?.forEach(bom => {
          const match = bom.components?.find(bc => bc.sku === mc.sku);
          if (match) originalBaseQty = match.baseQty;
        });
      }
      return { ...mc, perSet: originalBaseQty || 1, newQty: mc.newQty || 0, rentalQty: mc.rentalQty || 0 };
    });

    return {
      firmId: mData.firmId || orderData?.firmId || '',
      challanNo: mData.challanNo || `DC-${mData.mocNo}`,
      dispatchDate: mData.dispatchDate || mData.deliveryDate || new Date().toISOString().split('T')[0],
      deliveryAgent: mData.deliveryAgent || '',
      deliveryMode: mData.deliveryMode || 'Truck',
      coordinator: mData.coordinator || mData.preparedBy || '',
      dispatchedThrough: mData.dispatchedThrough || '',
      poNumber: mData.poNumber || '',
      poDate: mData.poDate || '',
      irnNo: mData.irnNo || '',
      billToCustomer: mData.billToCustomer || customerData?.company_name || mData.customerName || '',
      shipToCustomer: mData.shipToCustomer || orderData?.shippingAddress?.attention || mData.customerName || '',
      billToAddress: mData.billToAddress || formatAddress(orderData?.billingAddress),
      shipToAddress: mData.shipToAddress || mData.shippingAddress || '',
      billToGstin: mData.billToGstin || customerData?.gstin || customerData?.gst_number || '',
      salesperson: mData.salesperson || orderData?.salesPerson || mData.approvedBySalesperson || '',
      orderBy: mData.orderBy || orderData?.billingAddress?.attention || '',
      orderByContact: mData.orderByContact || orderData?.billingAddress?.phone || '',
      sitePerson: mData.sitePerson || orderData?.shippingAddress?.attention || '',
      sitePersonContact: mData.sitePersonContact || orderData?.shippingAddress?.phone || '',
      billToEmail: mData.billToEmail || orderData?.billingAddress?.email || '',
      shipToEmail: mData.shipToEmail || orderData?.shippingAddress?.email || '',
      itemCode: mData.itemCode || '',
      towerHeight: mData.towerHeight || '',
      totalMeter: mData.totalMeter || mData.totalMeterNumber || '',
      receivedByName: mData.receivedByName || '',
      receivedByContact: mData.receivedByContact || '',
      handoverDateTime: mData.handoverDateTime || '',
      taxInvoiceUrl: mData.taxInvoiceUrl || '',
      ewayBillUrl: mData.ewayBillUrl || '',
      einvoiceUrl: mData.einvoiceUrl || '',
      isDCConfirmed: mData.isDCConfirmed || false,
      approvedByFinance: mData.approvedByFinance || '',
      components: reconstructedComponents,
      vehicleProofUrl: mData.vehicleProofUrl || '',
      deliveryChallanUrl: mData.deliveryChallanUrl || '',
      bomSignedUrl: mData.bomSignedUrl || '',
    };
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
      const decodedUrl = decodeURIComponent(url);
      const pathParts = decodedUrl.split('?')[0].split('/');
      return pathParts[pathParts.length - 1];
    } catch (e) {
      return 'File';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  if (loading || !order) return <Spinner />;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/delivered')} style={backBtnStyle}><ArrowLeft size={24} /></button>
          <h2 style={{ margin: 0 }}>Order Tracking: {order.orderNumber}</h2>
        </div>
      </div>

      <div style={tabCardStyle}>
        <div style={tabContainerStyle}>
          <button
            style={activeTab === 'shipments' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('shipments')}
          >
            Shipment Tracking
          </button>
          <button
            style={activeTab === 'order' ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab('order')}
          >
            Order Preview
          </button>
        </div>

        <div style={tabContentStyle}>
          {activeTab === 'order' ? (
            customer ? <OrderPreview order={order} customer={customer} firms={firms} /> : <Spinner />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              {dispatches.map((dispatch, idx) => {
                const dcPreviewData = processDCPreviewData(dispatch, order, customer);
                const selectedFirm = firms.find(f => f.id === dcPreviewData.firmId);
                const isExpanded = (section) => expandedSections[dispatch.id]?.[section];

                return (
                  <div key={dispatch.id} style={shipmentCardStyle}>
                    {/* CARD HEADER */}
                    <div style={shipmentHeaderStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={shipmentBadgeStyle}>{idx + 1}</div>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }}>MOC: {dispatch.mocNo}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>DC: {dispatch.challanNo || 'Pending'}</div>
                        </div>
                      </div>
                      <div style={{ 
                        backgroundColor: dispatch.dispatchStatus === 'delivered' ? '#dcfce7' : '#eff6ff', 
                        color: dispatch.dispatchStatus === 'delivered' ? '#16a34a' : '#2563eb', 
                        padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' 
                      }}>
                        {dispatch.dispatchStatus === 'delivered' ? <CheckCircle size={14} /> : <Truck size={14} />}
                        {dispatch.dispatchStatus.toUpperCase()}
                      </div>
                    </div>

                    {/* TIMELINE */}
                    <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                      <HorizontalTimeline orderData={order} dispatchData={dispatch} title="Shipment Journey" />
                    </div>

                    {/* ACCORDION SECTIONS */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      
                      {/* 1. DELIVERY SUMMARY */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'summary')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Truck size={18} color="#2563eb" /> 1. Delivery Summary</div>
                          {isExpanded('summary') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('summary') && (
                          <div style={accordionContentStyle}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                              <div><label style={labelStyle}>Date of Delivery</label><p style={valueStyle}>{dispatch.deliveryDetails?.deliveryDate ? formatDate(dispatch.deliveryDetails.deliveryDate) : '---'}</p></div>
                              <div><label style={labelStyle}>Handover Details</label><p style={valueStyle}>{dispatch.handoverDateTime || '---'}</p></div>
                              <div><label style={labelStyle}>Receiver Name</label><p style={valueStyle}>{dispatch.deliveryDetails?.receiverName || '---'}</p></div>
                              <div><label style={labelStyle}>Receiver Contact</label><p style={valueStyle}>{dispatch.deliveryDetails?.receiverContact || '---'}</p></div>
                              <div><label style={labelStyle}>Confirmed By</label><p style={valueStyle}>{dispatch.deliveryDetails?.deliveryConfirmedBy || '---'}</p></div>
                              <div><label style={labelStyle}>Vehicle No.</label><p style={valueStyle}>{dispatch.vehicleNumber || '---'}</p></div>
                            </div>
                            
                            {/* POD Proof */}
                            {dispatch.deliveryDetails?.podUrl && (
                              <div style={{ marginTop: '20px' }}>
                                <label style={labelStyle}>Signed POD (Proof of Delivery)</label>
                                <a href={dispatch.deliveryDetails.podUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                                  <div style={{...attachmentIconStyle, backgroundColor: '#dcfce7', color: '#16a34a'}}><FileCheck size={20}/></div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={attachmentLabelStyle}>Delivery Proof</div>
                                    <div style={attachmentNameStyle}>{getFileNameFromUrl(dispatch.deliveryDetails.podUrl)}</div>
                                  </div>
                                  <ExternalLink size={18} />
                                </a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 2. MOC DETAIL */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'moc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Package size={18} color="#2563eb" /> 2. MOC Detail (BOM)</div>
                          {isExpanded('moc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('moc') && (
                          <div style={accordionContentStyle}>
                            <MOCPreviewContent mocData={dispatch} />
                          </div>
                        )}
                      </div>

                      {/* 3. DELIVERY CHALLAN */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'dc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FileText size={18} color="#2563eb" /> 3. Delivery Challan</div>
                          {isExpanded('dc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('dc') && (
                          <div style={accordionContentStyle}>
                            <DCPreviewContent dcData={dcPreviewData} selectedFirm={selectedFirm} />
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginTop: '20px' }}>
                              {dispatch.taxInvoiceUrl && (
                                <a href={dispatch.taxInvoiceUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                                  <div style={{...attachmentIconStyle, backgroundColor: '#c7d2fe', color: '#4f46e5'}}><FileText size={20}/></div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={attachmentLabelStyle}>Tax Invoice</div>
                                    <div style={attachmentNameStyle}>{getFileNameFromUrl(dispatch.taxInvoiceUrl)}</div>
                                  </div>
                                  <ExternalLink size={18} />
                                </a>
                              )}
                              {dispatch.ewayBillUrl && (
                                <a href={dispatch.ewayBillUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                                  <div style={{...attachmentIconStyle, backgroundColor: '#dcfce7', color: '#166534'}}><FileText size={20}/></div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={attachmentLabelStyle}>E-way Bill</div>
                                    <div style={attachmentNameStyle}>{getFileNameFromUrl(dispatch.ewayBillUrl)}</div>
                                  </div>
                                  <ExternalLink size={18} />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 4. QC & LOGISTICS */}
                      <div style={accordionStyle}>
                        <button onClick={() => toggleSection(dispatch.id, 'qc')} style={accordionBtnStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ShieldCheck size={18} color="#2563eb" /> 4. QC & Driver Proofs</div>
                          {isExpanded('qc') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {isExpanded('qc') && (
                          <div style={accordionContentStyle}>
                            <QCPreview formData={dispatch} />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginTop: '20px' }}>
                              {dispatch.vehicleProofUrl && (
                                <a href={dispatch.vehicleProofUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                                  <div style={{...attachmentIconStyle, backgroundColor: '#e0f2fe', color: '#0284c7'}}><Truck size={20}/></div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={attachmentLabelStyle}>Vehicle Proof</div>
                                    <div style={attachmentNameStyle}>{getFileNameFromUrl(dispatch.vehicleProofUrl)}</div>
                                  </div>
                                  <ExternalLink size={18} />
                                </a>
                              )}
                              {dispatch.bomSignedUrl && (
                                <a href={dispatch.bomSignedUrl} target="_blank" rel="noopener noreferrer" style={attachmentCardStyle}>
                                  <div style={{...attachmentIconStyle, backgroundColor: '#fee2e2', color: '#991b1b'}}><FileText size={20}/></div>
                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div style={attachmentLabelStyle}>Signed BOM</div>
                                    <div style={attachmentNameStyle}>{getFileNameFromUrl(dispatch.bomSignedUrl)}</div>
                                  </div>
                                  <ExternalLink size={18} />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- STYLES ---
const tabCardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: '600px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500', marginBottom: '0' };
const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };

const tabContainerStyle = { display: 'flex', borderBottom: '2px solid #f3f4f6', marginBottom: '20px', gap: '10px' };
const tabStyle = { padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#64748b', transition: 'all 0.2s' };
const activeTabStyle = { ...tabStyle, color: '#2563eb', borderBottom: '2px solid #2563eb', marginBottom: '-2px' };
const tabContentStyle = { minHeight: '400px' };

const shipmentCardStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
};

const shipmentHeaderStyle = {
  padding: '15px 20px',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const shipmentBadgeStyle = {
  backgroundColor: '#2563eb',
  color: '#fff',
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 'bold'
};

const accordionStyle = {
  borderBottom: '1px solid #f1f5f9'
};

const accordionBtnStyle = {
  width: '100%',
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  color: '#334155',
  transition: 'background 0.2s',
  textAlign: 'left'
};

const accordionContentStyle = {
  padding: '20px',
  backgroundColor: '#fdfdfd',
  borderTop: '1px solid #f8fafc'
};

const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  textDecoration: 'none',
  color: '#374151',
  transition: 'all 0.2s ease-in-out',
};

const attachmentIconStyle = {
  padding: '8px',
  backgroundColor: '#e0f2fe',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0284c7',
};

const attachmentLabelStyle = {
  fontSize: '11px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '2px',
};

const attachmentNameStyle = {
  fontSize: '13px',
  fontWeight: 'bold',
  color: '#1e293b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block'
};

export default DeliveredDetail;
